import { useLingui } from '@lingui/react/macro';
import { useLayoutEffect, useRef } from 'react';
import { z } from 'zod';

import {
  uploadFileInstruction,
  validateFileInstruction,
  type FileInstructionInput,
} from '@/modules/wallet/public';
import { useApiMutation } from '@/shared/data';
import { FileImportSheet } from '@/shared/patterns/file-import-sheet';
import { usePermissions } from '@/shared/permissions';
import { Input } from '@/shared/ui/input';

import { billingKeys } from './billing.queries';

/** Legacy CNAB import: choosing a file previews its name; server validation is optional. */
export function BillingImportDialog({ onClose }: { readonly onClose: () => void }) {
  const { t } = useLingui();
  const permissions = usePermissions();
  // eslint-disable-next-line lingui/no-unlocalized-strings -- Legacy permission identifiers, not UI text.
  const canRead = permissions.can('BillingAccount', 'Read');
  const allowed = useRef(canRead);
  useLayoutEffect(() => {
    allowed.current = canRead;
  }, [canRead]);
  const mutation = useApiMutation({
    mutationKey: ['billing', 'import'],
    mutationFn: (input: FileInstructionInput) => {
      if (!allowed.current) throw new Error(t`Acesso negado`);
      return uploadFileInstruction(input);
    },
    invalidate: [billingKeys.all],
    success: false,
    // FileImportSheet owns the single API error toast for submit and validation failures.
    error: false,
  });

  if (!canRead) return <p role="alert">{t`Acesso negado`}</p>;
  return (
    <FileImportSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t`Importar arquivo CNAB`}
      description={t`Confira o arquivo e o nome antes de confirmar a importação. A validação é opcional.`}
      accept="image/*,.pdf,.doc,.docx,.xlsx,.xsl,.rar,.zip,.rem"
      fields={{
        schema: z.object({ name: z.string().min(1, t`Nome: precisa ser preenchido`) }),
        defaults: { name: '' },
        onFile: (file, form) => form.setValue('name', file.name),
        render: (form) => (
          <label className="grid gap-2">
            {t`Nome`}
            <Input {...form.register('name')} aria-invalid={!!form.formState.errors.name} />
            {form.formState.errors.name && (
              <span role="alert">{form.formState.errors.name.message}</span>
            )}
          </label>
        ),
      }}
      validate={{
        run: (input) => {
          if (!allowed.current) throw new Error(t`Acesso negado`);
          return validateFileInstruction(input);
        },
        feedback: () => ({ tone: 'success', title: t`Arquivo validado com sucesso` }),
      }}
      submit={{
        run: (input) => mutation.mutateAsync(input),
        feedback: () => ({ tone: 'success', title: t`Arquivo CNAB importado com sucesso` }),
      }}
    />
  );
}
